// Partner Agreements — presentation helpers. The API returns backend status codes; the Partner only ever sees the
// friendly labels below (never Affiliate* / mapping terminology).

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 01-Oct-2026. Date-only values ("2026-10-01") are read as calendar dates, not shifted by time zone. */
export const formatAgreementDate = (value) => {
  if (!value) return '—';
  const text = String(value);
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(text);
  if (!Number.isFinite(date.getTime())) return '—';
  return `${String(date.getDate()).padStart(2, '0')}-${MONTHS[date.getMonth()]}-${date.getFullYear()}`;
};

/** 30-Sep-2026 14:05 (for timestamps such as the signed date/time). */
export const formatAgreementDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  return `${formatAgreementDate(value)} ${time}`;
};

const SIGNATURE_LABELS = {
  Pending: 'Signature Required',
  InProgress: 'Signature In Progress',
  Signed: 'Signed',
  Failed: 'Signature Failed',
  Revoked: 'Signature Revoked',
  NotRequired: 'Not Required',
};

const AGREEMENT_STATUS_LABELS = {
  Published: 'Published',
  Superseded: 'Superseded',
  Expired: 'Expired',
  Archived: 'Withdrawn',
};

const TONES = {
  success: 'text-emerald-700 bg-emerald-50',
  warning: 'text-amber-700 bg-amber-50',
  danger: 'text-red-700 bg-red-50',
  info: 'text-blue-700 bg-blue-50',
  neutral: 'text-gray-700 bg-gray-100',
};

/** Signature status label + badge tone for one agreement. Unsigned history rows read "Not Signed", not "Required". */
export const signatureStatusView = (agreement) => {
  const status = agreement?.signatureStatus || 'Pending';
  if (status === 'Signed') return { label: SIGNATURE_LABELS.Signed, tone: TONES.success };
  if (status === 'NotRequired' || !agreement?.requiresSignature) return { label: SIGNATURE_LABELS.NotRequired, tone: TONES.neutral };
  if (!agreement?.canSign) return { label: 'Not Signed', tone: TONES.neutral };
  if (status === 'Failed') return { label: SIGNATURE_LABELS.Failed, tone: TONES.danger };
  if (status === 'Revoked') return { label: SIGNATURE_LABELS.Revoked, tone: TONES.neutral };
  if (status === 'InProgress') return { label: SIGNATURE_LABELS.InProgress, tone: TONES.info };
  return { label: SIGNATURE_LABELS.Pending, tone: TONES.warning };
};

export const agreementStatusView = (agreement) => {
  const status = agreement?.agreementStatus || '';
  const label = AGREEMENT_STATUS_LABELS[status] || status || '—';
  const tone =
    status === 'Published' ? TONES.success : status === 'Expired' ? TONES.danger : TONES.neutral;
  return { label, tone };
};

export const agreementTitle = (agreement) =>
  agreement ? `${agreement.agreementName} v${agreement.agreementVersion}` : 'Partner Agreement';

export const normalizeAgreement = (row = {}) => ({
  affiliateAgreementMappingId: Number(row.affiliateAgreementMappingId) || 0,
  agreementName: row.agreementName || 'Partner Agreement',
  agreementVersion: row.agreementVersion || '',
  agreementStatus: row.agreementStatus || '',
  mappingStatus: row.mappingStatus || '',
  requiresSignature: Boolean(row.requiresSignature),
  signatureStatus: row.signatureStatus || '',
  effectiveDate: row.effectiveDate || null,
  expiryDate: row.expiryDate || null,
  assignedDateTime: row.assignedDateTime || null,
  signedDateTime: row.signedDateTime || null,
  agreementFileName: row.agreementFileName || 'Agreement.pdf',
  hasSignedDocument: Boolean(row.hasSignedDocument),
  signedDocumentFileName:
    row.signedDocumentFileName || `${(row.agreementFileName || 'Agreement').replace(/\.pdf$/i, '')}_Signed.pdf`,
  isCurrent: Boolean(row.isCurrent),
  isLatestVersion: Boolean(row.isLatestVersion),
  canSign: Boolean(row.canSign),
  signBlockedReason: row.signBlockedReason || '',
});

export const normalizeAgreementList = (payload) => {
  const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
  return list.map(normalizeAgreement);
};

export const normalizeAgreementDetails = (payload) => {
  if (!payload?.agreement) return null;
  return {
    agreement: normalizeAgreement(payload.agreement),
    signature: payload.signature || null,
    registeredName: payload.registeredName || '',
    history: Array.isArray(payload.history) ? payload.history.map(normalizeAgreement) : [],
  };
};

/** Signed copy name shown on download, e.g. Partner_Agreement_v2_ABC_Partner_Signed.pdf. */
export const signedFileNameOf = (details) =>
  details?.signature?.signedDocumentFileName || details?.agreement?.signedDocumentFileName || 'Signed_Agreement.pdf';

export const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const AGREEMENT_NOTIFICATION_TYPES = [
  'AgreementAssigned',
  'AgreementSignatureRequired',
  'AgreementVersionAssigned',
  'AgreementSignatureReminder',
  'AgreementExpiryReminder',
  'AgreementSigned',
];

export const AGREEMENTS_PATH = '/profile/agreements';
export const agreementDetailsPath = (mappingId) => `${AGREEMENTS_PATH}/${mappingId}`;

/** Type shown on a notification card; agreement codes are shown as "Partner Agreement". */
export const notificationTypeLabel = (type) =>
  AGREEMENT_NOTIFICATION_TYPES.includes(String(type || '')) ? 'Partner Agreement' : type || 'General';

/** Screen a notification should open, or null. Agreement notifications open Profile → Agreements. */
export const notificationRoute = (type) =>
  AGREEMENT_NOTIFICATION_TYPES.includes(String(type || '')) ? AGREEMENTS_PATH : null;
